/**
 * Updates the recently viewed products in localStorage.
 *
 * Every storage access here is guarded, mirroring assets/wishlist.js. localStorage
 * throws rather than returning null in more cases than it looks: it is unavailable
 * when a browser blocks site data, setItem throws once the origin's quota is full,
 * and a corrupted or hand-edited value makes JSON.parse throw. None of those are
 * recoverable, but none of them should take a page down either — this is a
 * convenience feature, so a failure degrades to "no history" and the callers all
 * treat an empty list as nothing to show.
 *
 * It matters more than the single product-page call suggests: predictive-search.js
 * reads this on every search-modal open to build its empty state.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 12;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch (error) {
      console.error('[RecentlyViewed] Error saving to storage:', error);
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch (error) {
      console.error('[RecentlyViewed] Error clearing storage:', error);
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products, or an empty list if storage is
   * unreadable or holds anything other than an array.
   */
  static getProducts() {
    try {
      const stored = localStorage.getItem(this.#STORAGE_KEY);
      const ids = stored ? JSON.parse(stored) : [];
      // Valid JSON is not necessarily the array the callers expect — a bare string
      // would survive the parse and then throw on .filter in addProduct.
      return Array.isArray(ids) ? ids.map(String) : [];
    } catch (error) {
      console.error('[RecentlyViewed] Error reading from storage:', error);
      return [];
    }
  }
}
